import * as React from 'react'
import {
  CheckboxIcon,
  ChevronDownIcon,
  ListBulletIcon,
  PilcrowIcon,
  QuoteIcon,
} from '@radix-ui/react-icons'
import { ToolbarToggleItem } from '@radix-ui/react-toolbar'
import { Editor } from '@tiptap/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { $toggleItem } from '..'
import { clsx } from 'clsx'
import { CodeBlockIcon } from '~/components/icons/code-block'
import { RichTextOption } from '@basehub/tiptap-api-utils/options'

export const hierarchyList: {
  name: RichTextOption | 'paragraph'
  label: string
  icon: React.ReactNode
  action: (editor: Editor) => void
  isActive: (editor: Editor) => boolean
}[] = [
  {
    name: 'h1',
    label: 'Heading 1',
    icon: <span className="text-xs w-4">H1</span>,
    action: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (editor: Editor) => editor.isActive('heading', { level: 1 }),
  },
  {
    name: 'h2',
    label: 'Heading 2',
    icon: <span className="text-xs w-4">H2</span>,
    action: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (editor: Editor) => editor.isActive('heading', { level: 2 }),
  },
  {
    name: 'h3',
    label: 'Heading 3',
    icon: <span className="text-xs w-4">H3</span>,
    action: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (editor: Editor) => editor.isActive('heading', { level: 3 }),
  },
  {
    name: 'h4',
    label: 'Heading 4',
    icon: <span className="text-xs w-4">H4</span>,
    action: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 4 }).run(),
    isActive: (editor: Editor) => editor.isActive('heading', { level: 4 }),
  },
  {
    name: 'h5',
    label: 'Heading 5',
    icon: <span className="text-xs w-4">H5</span>,
    action: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 5 }).run(),
    isActive: (editor: Editor) => editor.isActive('heading', { level: 5 }),
  },
  {
    name: 'h6',
    label: 'Heading 6',
    icon: <span className="text-xs w-4">H6</span>,
    action: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 6 }).run(),
    isActive: (editor: Editor) => editor.isActive('heading', { level: 6 }),
  },
  {
    name: 'paragraph',
    label: 'Paragraph',
    icon: <PilcrowIcon className="!w-4 !h-4" />,
    action: (editor: Editor) => editor.chain().focus().setParagraph().run(),
    isActive: (editor: Editor) => editor.isActive('paragraph'),
  },
  {
    name: 'codeBlock',
    label: 'Code block',
    icon: <CodeBlockIcon className="!w-4 !h-4" />,
    action: (editor: Editor) => editor.chain().focus().toggleCodeBlock().run(),
    isActive: (editor: Editor) => editor.isActive('codeBlock'),
  },
  {
    name: 'bulletList',
    label: 'Bullet list',
    icon: <ListBulletIcon className="!w-4 !h-4" />,
    action: (editor: Editor) => editor.chain().focus().toggleBulletList().run(),
    isActive: (editor: Editor) => editor.isActive('bulletList'),
  },
  {
    name: 'taskList',
    label: 'Task list',
    icon: <CheckboxIcon className="!w-4 !h-4" />,
    action: (editor: Editor) => editor.chain().focus().toggleTaskList().run(),
    isActive: (editor: Editor) => editor.isActive('taskList'),
  },
  {
    name: 'orderedList',
    label: 'Ordered list',
    icon: <span className="text-xs w-4">1.</span>,
    action: (editor: Editor) =>
      editor.chain().focus().toggleOrderedList().run(),
    isActive: (editor: Editor) => editor.isActive('orderedList'),
  },
  {
    name: 'blockquote',
    label: 'Blockquote',
    icon: <QuoteIcon className="!w-4 !h-4" />,
    action: (editor: Editor) => editor.chain().focus().toggleBlockquote().run(),
    isActive: (editor: Editor) => editor.isActive('blockquote'),
  },
]

export const HierarchyDropdownMenu = ({
  editor,
  containerElement,
  allowedHierarchy,
  activeHierarchy,
  blockId,
}: {
  editor: Editor
  containerElement?: React.RefObject<HTMLElement | null>
  allowedHierarchy: typeof hierarchyList
  activeHierarchy: (typeof hierarchyList)[number]
  blockId: string
}) => {
  return (
    <DropdownMenu aria-label="Hierarchy" modal={false}>
      <DropdownMenuTrigger asChild>
        <ToolbarToggleItem
          value={activeHierarchy.name}
          className={$toggleItem({
            active: false,
            className: 'flex items-center space-x-2 w-auto',
          })}
        >
          <span className="text-xs">{activeHierarchy.label}</span>
          {allowedHierarchy.length > 1 && (
            <ChevronDownIcon className="w-3 h-3" />
          )}
        </ToolbarToggleItem>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-rt-editor-popover-id={blockId}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
        collisionPadding={{
          top: 90,
        }}
        sideOffset={8}
        align="start"
        side="top"
        container={containerElement?.current}
        className="-translate-x-1 space-y-0.5"
      >
        {allowedHierarchy.map(({ name: item, icon, action, label }) => (
          <DropdownMenuItem
            key={item}
            className={clsx(
              activeHierarchy.name === item &&
                'text-dark-control-base bg-dark-elevated'
            )}
            onClick={() => action(editor)}
          >
            <span
              className={clsx(
                'flex items-center text-xs gap-2',
                activeHierarchy.name === item && 'text-dark-control-base'
              )}
            >
              {icon}
              {label}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
